import { useMemo, useState } from "react";
import { usePatchAppConfig, type AppConfig } from "@/api/admin/app-config";
import { useConfirmedChange } from "../confirmChange";
import type { Risk } from "../risk";
import { useAdminT } from "../useAdminT";
import { BTN, BTN_DISABLED, COL, ERROR, LABEL, TEXTAREA } from "./styles";

interface Props {
  settingKey: string;
  name: string;
  risk: Risk;
  /** app_config row; absent when the key has no row yet (code default applies). */
  row?: AppConfig;
}

/** app_config dial: boolean toggle or JSON textarea. A missing row is created by the same PATCH (upsert). */
export function DialEditor({ settingKey, name, risk, row }: Props): JSX.Element {
  const t = useAdminT();
  const patch = usePatchAppConfig();
  const { confirmChange, modal } = useConfirmedChange();
  const isBool = typeof row?.value === "boolean";
  const [draft, setDraft] = useState(row ? JSON.stringify(row.value, null, 2) : "");
  const { parsed, valid } = useMemo(() => {
    try {
      return { parsed: JSON.parse(draft) as unknown, valid: true };
    } catch {
      return { parsed: undefined, valid: false };
    }
  }, [draft]);
  const dirty = JSON.stringify(parsed) !== JSON.stringify(row?.value);

  const show = (v: unknown): string => (v === undefined ? t("shell.not_set") : JSON.stringify(v));
  const save = (value: unknown): void =>
    confirmChange({ name, risk }, show(row?.value), show(value), () =>
      patch.mutate({ key: settingKey, value }, {})
    );

  return (
    <div style={COL}>
      {row === undefined && <div style={LABEL}>{t("shell.not_set")}</div>}
      {isBool ? (
        <label>
          <input
            aria-label={settingKey}
            type="checkbox"
            checked={row?.value as boolean}
            onChange={(e) => save(e.target.checked)}
          />
        </label>
      ) : (
        <>
          <textarea
            aria-label={settingKey}
            style={TEXTAREA}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {!valid && draft !== "" && <div style={ERROR}>{t("app_config.json_invalid")}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              aria-label={`save ${settingKey}`}
              style={valid && dirty ? BTN : BTN_DISABLED}
              disabled={!valid || !dirty}
              onClick={() => save(parsed)}
            >
              {t("common.save")}
            </button>
          </div>
        </>
      )}
      {modal}
    </div>
  );
}
